import { Download } from "@mui/icons-material";
import { Alert, Box, Chip, IconButton, Sheet, Stack, Tooltip, Typography } from "@mui/joy";
import { useMutation } from "@tanstack/react-query";
import { Outlet, useNavigate, useParams } from "@tanstack/react-router";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import {
  ApiQuest,
  ApiQuestDetail,
  ApiQuestEntryStaff,
  ApiQuestStats,
  ApiQuestStatsTrigger,
} from "griptonite-api-client/types";
import { ReactNode, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import api from "@/core/api";
import { useQuestEntriesQuery, useQuestQuery, useQuestStatsQuery } from "@/hooks/quests";
import { useCurrentVenueUuid } from "@/hooks/venues";
import downloadBlob from "@/utils/downloadBlob";
import formatNumber from "@/utils/formatNumber";

import Avatar from "../Avatar";
import SortableTable, { normalizeForSearch, StickySearchBar } from "../Loyalty/SortableTable";

export const QuestEntryStatusChip = ({ status }: { status: ApiQuestEntryStaff["status"] }) =>
  status === "COMPLETED" ? (
    <Chip size="sm" variant="soft" color="success">
      Completed
    </Chip>
  ) : (
    <Chip size="sm" variant="soft" color="primary">
      Active
    </Chip>
  );

const columnHelper = createColumnHelper<ApiQuestEntryStaff>();

// Score and milestones only mean something on a quest that scores points.
const getColumns = (quest: ApiQuest | ApiQuestDetail) => [
  columnHelper.accessor((entry) => entry.user.name, {
    id: "name",
    header: "Name",
    sortingFn: "text",
    meta: { minWidth: 200 },
    cell: ({ row }) => (
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Avatar user={row.original.user} size={32} />
        <Typography fontWeight="bold" noWrap sx={{ minWidth: 0 }}>
          {row.original.user.name}
        </Typography>
      </Stack>
    ),
  }),
  ...(quest.type === "LEADERBOARD_POINTS"
    ? [
        // Undefined rather than null, so climbers who aren't scored sort last in both directions,
        // as the API sorts them.
        columnHelper.accessor((entry) => entry.score ?? undefined, {
          id: "score",
          header: "Points",
          sortUndefined: "last",
          meta: { width: 100 },
          cell: ({ getValue }) => getValue() ?? <Typography textColor="neutral.500">—</Typography>,
        }),
        columnHelper.accessor((entry) => entry.thresholdsReached.length, {
          id: "milestones",
          header: "Milestones",
          meta: { width: 120 },
          cell: ({ getValue }) =>
            "pointsThresholds" in quest && quest.pointsThresholds
              ? `${getValue()} / ${quest.pointsThresholds.length}`
              : getValue(),
        }),
      ]
    : []),
  columnHelper.accessor("creditsEarned", {
    header: "Credits earned",
    meta: { width: 150 },
    cell: ({ getValue }) => <Typography fontWeight="bold">{getValue()}</Typography>,
  }),
  columnHelper.accessor("creationTime", {
    header: "Joined",
    sortingFn: "datetime",
    meta: { width: 170 },
    cell: ({ getValue }) => (
      <Typography noWrap level="inherit">
        {format(new Date(getValue()), "d MMM yyyy, HH:mm")}
      </Typography>
    ),
  }),
];

// Clicking a row opens the entry in the side panel.
const EntriesTable = ({
  quest,
  entries,
  venueUuid,
}: {
  quest: ApiQuest | ApiQuestDetail;
  entries: ApiQuestEntryStaff[];
  venueUuid: string;
}) => {
  const navigate = useNavigate();
  const openEntryUuid = useParams({ strict: false }).entryUuid;
  const columns = useMemo(() => getColumns(quest), [quest]);
  const table = useReactTable({
    columns,
    data: entries,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    initialState: { sorting: [{ id: "creditsEarned", desc: true }] },
  });

  return (
    <SortableTable
      table={table}
      backgroundColor={"white"}
      isRowOpen={(entry) => entry.uuid === openEntryUuid}
      onRowClick={(entry) =>
        navigate({
          to: "/gym/$venueUuid/loyalty/quests/$questUuid/entries/$entryUuid",
          params: { venueUuid, questUuid: quest.uuid, entryUuid: entry.uuid },
        })
      }
    />
  );
};

const StatCard = ({
  label,
  value,
  detail,
}: {
  label: string;
  value: number | undefined;
  detail?: string;
}) => (
  <Sheet variant="outlined" sx={{ flex: 1, minWidth: 0, borderRadius: "lg", px: 2.5, py: 2 }}>
    <Typography level="title-sm" textColor="neutral.600">
      {label}
    </Typography>
    <Typography level="h2">{value === undefined ? "–" : formatNumber(value)}</Typography>
    {detail && (
      <Typography level="body-sm" textColor="neutral.600">
        {detail}
      </Typography>
    )}
  </Sheet>
);

// Cells share one border, with hairline dividers that stay tidy when the grid wraps. Each
// cell's shadow fills the 1px gaps, so empty slots in a short last row stay white.
//
// With `fourUpMinWidth` set the group holds exactly four cells and is always 4x1 or 2x2, never
// 3+1. That is a container query on a wrapper, so it follows the space left beside the sidebar
// rather than the viewport. Without it, the grid fills as many `minCellWidth` columns as fit.
const StatGroup = ({
  minCellWidth,
  fourUpMinWidth,
  children,
}: {
  minCellWidth?: number;
  fourUpMinWidth?: number;
  children: ReactNode;
}) => {
  const grid = (
    <Box
      sx={(theme) => ({
        display: "grid",
        gridTemplateColumns:
          fourUpMinWidth !== undefined
            ? "repeat(2, 1fr)"
            : `repeat(auto-fit, minmax(${minCellWidth}px, 1fr))`,
        ...(fourUpMinWidth !== undefined && {
          [`@container (min-width: ${fourUpMinWidth}px)`]: {
            gridTemplateColumns: "repeat(4, 1fr)",
          },
        }),
        gap: "1px",
        overflow: "hidden",
        borderRadius: "lg",
        border: "1px solid",
        borderColor: "neutral.outlinedBorder",
        backgroundColor: "white",
        "& > *": { boxShadow: `0 0 0 1px ${theme.vars.palette.neutral.outlinedBorder}` },
      })}
    >
      {children}
    </Box>
  );
  return fourUpMinWidth !== undefined ? (
    <Box sx={{ containerType: "inline-size" }}>{grid}</Box>
  ) : (
    grid
  );
};

const HeadlineStat = ({
  label,
  value,
  detail,
}: {
  label: string;
  value: number | undefined;
  detail?: string;
}) => (
  <Box sx={{ backgroundColor: "white", px: 2.5, py: 2 }}>
    <Typography level="title-sm" textColor="neutral.600">
      {label}
    </Typography>
    <Typography level="h2">{value === undefined ? "–" : formatNumber(value)}</Typography>
    <Typography level="body-sm" textColor="neutral.600" sx={{ minHeight: "1.5em" }}>
      {detail}
    </Typography>
  </Box>
);

// Triggers are counted over the whole quest, so a zero is real rather than missing.
const TriggerStat = ({
  label,
  trigger,
}: {
  label: string;
  trigger: ApiQuestStatsTrigger | undefined;
}) => {
  const total = trigger ? trigger.rewarded + trigger.unrewarded : undefined;
  return (
    <Box sx={{ backgroundColor: "white", px: 2.5, py: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
        <Typography level="title-sm" textColor="neutral.600">
          {label}
        </Typography>
        <Typography level="title-lg">{total === undefined ? "–" : formatNumber(total)}</Typography>
      </Stack>
    </Box>
  );
};

const QuestStats = ({ stats }: { stats: ApiQuestStats | undefined }) => (
  <Stack gap={1.5} sx={{ pt: 2 }}>
    <StatGroup minCellWidth={100}>
      <HeadlineStat label="Participants" value={stats?.participants} />
      <HeadlineStat
        label="Visits"
        value={stats?.visits.total}
        detail={stats && `${formatNumber(stats.visits.inBoost)} during boost time`}
      />
      <HeadlineStat label="Climbs logged" value={stats?.climbsLogged} />
    </StatGroup>
    <StatGroup fourUpMinWidth={800}>
      <TriggerStat label="Beta videos" trigger={stats?.betaVideos} />
      <TriggerStat label="Grade feedback" trigger={stats?.gradeFeedback} />
      <TriggerStat label="Route ratings" trigger={stats?.starRatings} />
      <TriggerStat label="Trait votes" trigger={stats?.traitVotes} />
    </StatGroup>
    <Typography level="body-sm" textColor="neutral.600">
      Credit cost:{" "}
      <Typography fontWeight="lg" textColor="neutral.700">
        {stats ? formatNumber(stats.credits.net) : "–"}
      </Typography>
      {stats &&
        ` (${formatNumber(stats.credits.byKind.POINTS_THRESHOLD)} from milestones, ${formatNumber(stats.credits.byKind.BONUS)} from bonuses, ${formatNumber(stats.credits.byKind.DISQUALIFICATION)} from disqualifications)`}
    </Typography>
  </Stack>
);

export default function QuestEntries({ questUuid }: { questUuid: string }) {
  const venueUuid = useCurrentVenueUuid();
  const { data: quest } = useQuestQuery(questUuid, venueUuid);
  const entriesQuery = useQuestEntriesQuery(questUuid);
  const hasStats = quest?.type === "LEADERBOARD_POINTS";
  const statsQuery = useQuestStatsQuery(questUuid, hasStats);

  // The input updates as the user types; the filter waits for a pause.
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const updateSearchQuery = useDebouncedCallback(setSearchQuery, 150);

  const entries = useMemo(() => {
    const query = normalizeForSearch(searchQuery.trim());
    if (!query) return entriesQuery.data;
    return entriesQuery.data?.filter(
      ({ user }) =>
        normalizeForSearch(user.name).includes(query) ||
        normalizeForSearch(user.email).includes(query),
    );
  }, [entriesQuery.data, searchQuery]);

  // Totals for the whole quest, whatever the search.
  const totalCreditsEarned = useMemo(
    () => entriesQuery.data?.reduce((total, entry) => total + entry.creditsEarned, 0),
    [entriesQuery.data],
  );

  // The file has every entry, whatever the search. Only points quests can be exported.
  const exportMutation = useMutation({
    mutationFn: async () => {
      const blob = await api.quests.getQuestEntriesCsv(questUuid);
      downloadBlob(blob, `quest-entries-${questUuid}-${format(new Date(), "yyyy-MM-dd")}.csv`);
    },
    onError: () => alert("Failed to export the entries. Please try again"),
  });

  let content;
  if (entriesQuery.error) {
    content = <Alert color="danger">Couldn’t load the entries.</Alert>;
  } else if (venueUuid && quest && entries) {
    if (!entriesQuery.data?.length) {
      content = <Typography textColor="neutral.500">No entries yet.</Typography>;
    } else if (!entries.length) {
      content = <Typography textColor="neutral.500">No entries match this search.</Typography>;
    } else {
      content = <EntriesTable quest={quest} entries={entries} venueUuid={venueUuid} />;
    }
  }

  // The panel for a chosen entry renders in the outlet, beside the table.
  return (
    <Stack direction="row" gap={1.5} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          overflowY: "auto",
          backgroundColor: "white",
          borderRadius: 8,
          px: 2,
          pb: 2,
        }}
      >
        {hasStats ? (
          statsQuery.error ? (
            <Alert color="danger" sx={{ mt: 2 }}>
              Couldn’t load the quest stats.
            </Alert>
          ) : (
            <QuestStats stats={statsQuery.data} />
          )
        ) : (
          <Stack direction="row" gap={1.5} sx={{ pt: 2 }}>
            <StatCard label="Entered" value={entriesQuery.data?.length} />
            <StatCard label="Credits earned" value={totalCreditsEarned} />
          </Stack>
        )}
        <StickySearchBar
          backgroundColor="white"
          placeholder="Search by name or email"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            updateSearchQuery(event.target.value);
          }}
          actions={
            hasStats && (
              <Tooltip title="Download all entries as CSV">
                <IconButton
                  onClick={() => exportMutation.mutate()}
                  disabled={exportMutation.isPending}
                  loading={exportMutation.isPending}
                >
                  <Download />
                </IconButton>
              </Tooltip>
            )
          }
        />
        {content}
      </Stack>
      <Outlet />
    </Stack>
  );
}
